import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import useAuth from '../../hooks/useAuth';
import { useNotifications } from '../../context/NotificationsContext';
import SchoolYearModal from '../modal/SchoolYearModal';
import { FamilyMember } from '../profile/FamilyMembers';
import {
  SCHOOL_YEAR_PROMOTED,
  SchoolYearNotification,
  fetchSchoolYearNotifications,
  latestPerStudent,
  markNotificationRead,
} from '../../utils/school';

/**
 * Sorveglia le notifiche di inizio anno scolastico ancora da leggere e apre la
 * relativa modale su tutte le pagine famiglia. Le promozioni vanno in cima alla coda.
 * Chi ignora la modale la fa passare in "read": non si ripropone qui, ma resta
 * pendente (non "accepted") e blocca lo studente in prenotazione.
 */
const SchoolYearGate: React.FC = () => {
  const { auth } = useAuth();
  const { refreshToken, refreshNotifications } = useNotifications();
  const [queue, setQueue] = useState<SchoolYearNotification[]>([]);
  const [sons, setSons] = useState<FamilyMember[]>([]);

  const load = useCallback(() => {
    Promise.all([fetchSchoolYearNotifications(), api.get('/family')])
      .then(([schoolYearNotifications, familyRes]) => {
        const familySons: FamilyMember[] = familyRes.data.sons || [];
        // Prima si tiene solo la notifica più recente per figlio, poi si filtra
        // per "unread": se la più recente è già stata vista non si riapre nulla,
        // anche se un duplicato più vecchio è rimasto non letto.
        const pending: SchoolYearNotification[] = latestPerStudent(schoolYearNotifications).filter(
          (notification) => notification.status === 'unread',
        );

        // Notifiche riferite a studenti non più presenti: chiuse senza mostrare nulla.
        // Qui si parte dalla lista completa, così si chiudono anche i duplicati.
        const orphans = schoolYearNotifications.filter(
          (n) => n.status === 'unread' && !familySons.some((son) => son.id === n.metadata.student_id),
        );
        orphans.forEach((n) =>
          markNotificationRead(n.id).catch((err) =>
            console.error(`Errore nel segnare la notifica ${n.id} come letta`, err),
          ),
        );

        const actionable = pending
          .filter((n) => familySons.some((son) => son.id === n.metadata.student_id))
          .sort((a, b) => Number(b.type === SCHOOL_YEAR_PROMOTED) - Number(a.type === SCHOOL_YEAR_PROMOTED));

        setSons(familySons);
        setQueue(actionable);
      })
      .catch((error) => {
        console.error('Errore nel recupero delle notifiche di inizio anno scolastico:', error);
      });
  }, []);

  useEffect(() => {
    if (!auth.isFamily) return;
    load();
  }, [auth.isFamily, refreshToken, load]);

  if (!auth.isFamily || queue.length === 0) return null;

  const current = queue[0];
  const student = sons.find((son) => son.id === current.metadata.student_id);
  if (!student) return null;

  const handleDone = () => {
    setQueue((prev) => prev.slice(1));
    refreshNotifications();
  };

  return (
    <SchoolYearModal
      key={current.id}
      notification={current}
      student={student}
      onDone={handleDone}
    />
  );
};

export default SchoolYearGate;
